import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faListCheck,
  faCalendarDay,
  faNoteSticky,
  faUserGroup,
  faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import { parseQuickTask, memberSlug, resolveResponsavel } from "../utils/quickTaskParser";
import { getTaskPriorityLabel } from "../utils/taskPriority";
import "./DashboardCommandBar.css";

const nomeDoMembro = (m) => m?.nome || m?.descricao || m?.email || "Sem nome";

const normalize = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Barra de comando sempre visivel no topo da Dashboard - promove o que
 * antes era o CommandPalette (modal escondido atras de Ctrl+K) pro mesmo
 * padrao do "Ask anything" do GitHub: saudacao + campo unico + chips de
 * atalho. Herdou toda a logica de parse/autocomplete/resolucao de @pessoa
 * do CommandPalette original (ver utils/quickTaskParser.js), que foi
 * descontinuado por ser a unica tela que o usava.
 */
const DashboardCommandBar = forwardRef(
  ({ nomeUsuario, membros = [], currentUserId, onSubmit, onPlanejar, onVerTime, onAbrirOkrs, onNovaNota }, ref) => {
    const [value, setValue] = useState("");
    const [enviando, setEnviando] = useState(false);
    const inputRef = useRef(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    const parsed = useMemo(() => parseQuickTask(value), [value]);
    const responsavel = useMemo(
      () => resolveResponsavel(parsed.responsavelSlug, membros, currentUserId),
      [parsed.responsavelSlug, membros, currentUserId]
    );

    const tokenAtual = value.split(/\s+/).pop() || "";
    const digitandoPessoa = tokenAtual.startsWith("@") && tokenAtual.length > 1;
    const sugestoes = useMemo(() => {
      if (!digitandoPessoa) return [];
      const alvo = normalize(tokenAtual.slice(1));
      return membros.filter((m) => normalize(nomeDoMembro(m)).includes(alvo)).slice(0, 5);
    }, [digitandoPessoa, tokenAtual, membros]);

    function escolherSugestao(membro) {
      const partes = value.split(/\s+/);
      partes[partes.length - 1] = `@${memberSlug(membro)}`;
      setValue(partes.join(" ") + " ");
      inputRef.current?.focus();
    }

    async function enviar() {
      if (!parsed.descricao || enviando) return;
      // Mesma trava do CommandPalette: @algo sem bater em uma pessoa so nao
      // envia, nunca assume "voce" por padrao quando um nome foi digitado.
      if (parsed.responsavelSlug && (responsavel.ambiguo || responsavel.naoEncontrado)) return;
      setEnviando(true);
      try {
        await onSubmit(parsed, value, responsavel);
        setValue("");
      } finally {
        setEnviando(false);
      }
    }

    function handleKeyDown(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        if (sugestoes.length > 0) {
          escolherSugestao(sugestoes[0]);
          return;
        }
        enviar();
      }
    }

    const bloqueado = Boolean(parsed.responsavelSlug && (responsavel.ambiguo || responsavel.naoEncontrado));

    return (
      <div className="dcb-wrap">
        <p className="dcb-greeting">Ola, {nomeUsuario}</p>

        <div className="dcb-bar">
          <div className="dcb-input-wrap">
            <input
              ref={inputRef}
              className="dcb-input"
              placeholder='O que voce quer fazer? Ex.: "ligar pro cliente X amanha @joao !alta"'
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {sugestoes.length > 0 && (
              <div className="dcb-sugestoes">
                {sugestoes.map((m) => (
                  <button
                    type="button"
                    key={m.id_usuario ?? m.id}
                    className="dcb-sugestao-item"
                    onClick={() => escolherSugestao(m)}
                  >
                    {nomeDoMembro(m)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="dcb-send"
            onClick={enviar}
            disabled={!parsed.descricao || enviando || bloqueado}
            title="Criar tarefa"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>

        {value.trim() && (
          <div className="dcb-preview">
            <span><b>{parsed.descricao || "—"}</b></span>
            <span>
              Responsavel:{" "}
              {responsavel.membro ? (
                nomeDoMembro(responsavel.membro)
              ) : responsavel.ambiguo ? (
                <span className="dcb-aviso">ambiguo — seja mais especifico</span>
              ) : responsavel.naoEncontrado ? (
                <span className="dcb-aviso">"@{parsed.responsavelSlug}" nao encontrado</span>
              ) : (
                "voce"
              )}
            </span>
            <span>Prioridade: {getTaskPriorityLabel(parsed.prioridade)}</span>
            {parsed.vencimento && <span>Vencimento: {parsed.vencimento}</span>}
          </div>
        )}

        <div className="dcb-hints">
          <span><b>#</b>projeto</span>
          <span><b>@</b>pessoa</span>
          <span><b>!</b>prioridade</span>
          <span><b>hoje</b>/<b>amanha</b>/<b>dd/mm/aaaa</b></span>
        </div>

        <div className="dcb-chips">
          <button type="button" onClick={() => inputRef.current?.focus()}>
            <FontAwesomeIcon icon={faListCheck} /> Nova tarefa
          </button>
          <button type="button" onClick={onPlanejar}>
            <FontAwesomeIcon icon={faCalendarDay} /> Planejar meu dia
          </button>
          <button type="button" onClick={onNovaNota}>
            <FontAwesomeIcon icon={faNoteSticky} /> Nova nota
          </button>
          <button type="button" onClick={onAbrirOkrs}>
            <FontAwesomeIcon icon={faBullseye} /> OKRs
          </button>
          <button type="button" onClick={onVerTime}>
            <FontAwesomeIcon icon={faUserGroup} /> Ver time
          </button>
        </div>
      </div>
    );
  }
);

export default DashboardCommandBar;
